import { Children, cloneElement, useId, useEffect, useState, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { IconButton } from "./IconButton";
import { WIDTH } from "../features/foundation";

type TabProps = {
    children?: React.ReactNode;
    schema: any[];
    value?: number;
    onChange?: (index: number) => void;
};

type TabPanelProps = {
    children?: React.ReactNode;
    isActive?: Boolean;
};

const TabPanel = (props: TabPanelProps) => {
    const { children, isActive } = props;
    return <div hidden={!isActive}>{children}</div>;
};

const TabItem = (props: any) => {
    const { t } = useTranslation();

    const { data, index, current, handleClickTab } = props;
    const { label, disabled, visible, width } = data;
    const active = current === index;

    if (visible === false) return null;
    return (
        <li
            className={classNames(
                "flex cursor-pointer items-center truncate transition rounded-t border-b-2 border-transparent h-8 px-4 min-w-fit",
                active && "bg-primary text-primary-contrast border-primary-thick",
                disabled && "text-lightgray",
                width && WIDTH[width as keyof typeof WIDTH],
            )}
            onClick={() => handleClickTab(data, index)}
        >
            {typeof label === "string" ? t(label) : label}
        </li>
    );
};

const StartFlag = ({ setOverflow }: any) => {
    const ref = useRef(null);

    useLayoutEffect(() => {
        if (!ref.current) return;

        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                setOverflow(!(entry.intersectionRatio > 0));
            });
        });

        io.observe(ref.current);

        return () => {
            io.disconnect();
        };
    }, []);

    return <li ref={ref} />;
};

const EndFlag = ({ setOverflow }: any) => {
    const ref = useRef(null);

    useLayoutEffect(() => {
        if (!ref.current) return;

        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                setOverflow(!(entry.intersectionRatio > 0));
            });
        });

        io.observe(ref.current);

        return () => {
            io.disconnect();
        };
    }, []);

    return <li ref={ref} />;
};

export const Tab = (props: TabProps) => {
    const { children, schema, value, onChange } = props;
    const id = useId();
    const [start, setStart] = useState(false);
    const [end, setEnd] = useState(false);
    const _context = useRef<any>({ ul: null });

    const overflow = start || end;

    const [_value, _setValue] = useState<number>(() => {
        if (value !== undefined) return value;
        if (schema.some((_) => _.active)) return schema.findIndex((_) => _.active);
        return 0;
    });

    useEffect(() => {
        if (value === undefined) return;
        if (value === _value) return;
        _setValue(value);
    }, [value]);

    const handleClickTab = (item: any, index: number) => {
        const { disabled } = item;
        if (disabled) return;

        if (onChange) {
            let t: any = onChange(index);
            if (t === false) {
                //
            } else {
                _setValue(index);
            }
        } else {
            _setValue(index);
        }
    };

    const handleClickPrev = () => {
        if (!_context.current.ul) return;
        _context.current.ul.scrollLeft = 0;
    };

    const handleClickNext = () => {
        if (!_context.current.ul) return;
        _context.current.ul.scrollLeft = _context.current.ul.scrollWidth;
    };

    return (
        <div className="flex flex-col w-full space-y-4">
            <div className="flex border-b items-center gap-2">
                {/* {overflow && <IconButton icon="left" size="xs" onClick={handleClickPrev} />} */}
                <ul
                    ref={(ref) => {
                        if (!ref) return;
                        _context.current.ul = ref;
                    }}
                    className="flex flex-1 overflow-x-auto"
                    // className="flex flex-1 overflow-x-scroll scrollbar-hide scroll-smooth"
                >
                    {/* <StartFlag setOverflow={setStart} /> */}
                    {schema.map((data, index) => {
                        return (
                            <TabItem
                                key={id + index}
                                data={data}
                                index={index}
                                current={_value}
                                handleClickTab={handleClickTab}
                            />
                        );
                    })}
                    {/* <EndFlag setOverflow={setEnd} /> */}
                </ul>
                {/* {overflow && <IconButton icon="right" size="xs" onClick={handleClickNext} />} */}
            </div>

            <div>
                {Children.toArray(children)
                    .filter((child) => child)
                    .map((child: any, i) => {
                        return cloneElement(child, { isActive: _value === i });
                    })}
            </div>
        </div>
    );
};

Tab.Panel = TabPanel;
